import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  EventDto,
  EventPatchDto,
  GameDto,
  GameTagDto,
  GroupDto,
  UserDto,
} from '../../api/types';

export type EntitiesState = {
  events: {
    items: EventDto[];
  };
  groups: {
    items: GroupDto[];
  };
  users: {
    items: UserDto[];
  };
  games: {
    items: GameDto[];
    tags: GameTagDto[];
    favoriteIds: string[];
  };
};

const initialState: EntitiesState = {
  events: { items: [] },
  groups: { items: [] },
  users: { items: [] },
  games: { items: [], tags: [], favoriteIds: [] },
};

function mergeByIdAndUpdatedAt<T extends { id: string; updatedAt: string }>(
  existing: T[],
  incoming: T[],
): T[] {
  const map = new Map(existing.map((item) => [item.id, item] as const));

  for (const item of incoming) {
    const current = map.get(item.id);
    if (!current) {
      map.set(item.id, item);
      continue;
    }

    // Use >= to prefer incoming data when timestamps are equal
    if (new Date(item.updatedAt).getTime() >= new Date(current.updatedAt).getTime()) {
      map.set(item.id, item);
    }
  }

  return Array.from(map.values());
}

const entitiesSlice = createSlice({
  name: 'entities',
  initialState,
  reducers: {
    upsertEvents(state, action: PayloadAction<EventDto[]>) {
      state.events.items = mergeByIdAndUpdatedAt(state.events.items, action.payload);
    },
    upsertEvent(state, action: PayloadAction<EventDto>) {
      const event = action.payload;
      const idx = state.events.items.findIndex((e) => e.id === event.id);
      if (idx === -1) {
        state.events.items.push(event);
      } else {
        const current = state.events.items[idx];
        if (new Date(event.updatedAt).getTime() >= new Date(current.updatedAt).getTime()) {
          state.events.items[idx] = event;
        }
      }
    },
    upsertGroups(state, action: PayloadAction<GroupDto[]>) {
      state.groups.items = mergeByIdAndUpdatedAt(state.groups.items, action.payload);
    },
    upsertGroup(state, action: PayloadAction<GroupDto>) {
      const group = action.payload;
      const idx = state.groups.items.findIndex((g) => g.id === group.id);
      if (idx === -1) {
        state.groups.items.push(group);
      } else {
        const current = state.groups.items[idx];
        if (new Date(group.updatedAt).getTime() >= new Date(current.updatedAt).getTime()) {
          state.groups.items[idx] = group;
        }
      }
    },
    removeGroup(state, action: PayloadAction<string>) {
      state.groups.items = state.groups.items.filter((g) => g.id !== action.payload);
    },
    removeEvent(state, action: PayloadAction<string>) {
      state.events.items = state.events.items.filter((e) => e.id !== action.payload);
    },
    patchEvent(state, action: PayloadAction<EventPatchDto>) {
      const patch = action.payload;
      const idx = state.events.items.findIndex((e) => e.id === patch.id);
      if (idx === -1) return;

      const current = state.events.items[idx];
      if (new Date(patch.updatedAt).getTime() < new Date(current.updatedAt).getTime()) return;

      state.events.items[idx] = {
        ...current,
        ...patch,
        participants: patch.participants ?? current.participants,
      };
    },
    setGroups(state, action: PayloadAction<GroupDto[]>) {
      state.groups.items = action.payload;
    },
    setUsers(state, action: PayloadAction<UserDto[]>) {
      state.users.items = action.payload;
    },
    upsertUser(state, action: PayloadAction<UserDto>) {
      const user = action.payload;
      const idx = state.users.items.findIndex((item) => item.telegramId === user.telegramId);
      if (idx === -1) {
        state.users.items.push(user);
      } else {
        state.users.items[idx] = user;
      }
    },
    setGames(state, action: PayloadAction<GameDto[]>) {
      state.games.items = action.payload;
    },
    upsertGame(state, action: PayloadAction<GameDto>) {
      const game = action.payload;
      const idx = state.games.items.findIndex((g) => g.id === game.id);
      if (idx === -1) {
        state.games.items.push(game);
      } else {
        state.games.items[idx] = game;
      }
    },
    removeGame(state, action: PayloadAction<string>) {
      state.games.items = state.games.items.filter((g) => g.id !== action.payload);
    },
    setGameTags(state, action: PayloadAction<GameTagDto[]>) {
      state.games.tags = action.payload;
    },
    setFavoriteGameIds(state, action: PayloadAction<string[]>) {
      state.games.favoriteIds = action.payload;
    },
    addFavoriteGameId(state, action: PayloadAction<string>) {
      if (!state.games.favoriteIds.includes(action.payload)) {
        state.games.favoriteIds.push(action.payload);
      }
    },
    removeFavoriteGameId(state, action: PayloadAction<string>) {
      state.games.favoriteIds = state.games.favoriteIds.filter((id) => id !== action.payload);
    },
    resetEntities(state) {
      state.events.items = [];
      state.groups.items = [];
      state.users.items = [];
      state.games.items = [];
      state.games.tags = [];
      state.games.favoriteIds = [];
    },
  },
});

export const entitiesReducer = entitiesSlice.reducer;
export const entitiesActions = entitiesSlice.actions;
